import { useLiveQuery } from 'dexie-react-hooks'
import { db, type DiningTable, type Order } from '../lib/db'
import { elapsedText, money } from '../lib/money'
import { useNow } from '../lib/hooks'
import { Sheet } from './Sheet'
import { TableIcon } from './TableIcon'

export type PickMode = 'move' | 'merge'

interface Props {
  mode: PickMode
  /** Bàn đang đứng — không cho chọn chính nó. */
  fromTableId: string | null
  onPick: (table: DiningTable, targetOrder: Order | undefined) => void | Promise<void>
  onClose: () => void
}

/**
 * Chọn bàn đích cho việc chuyển hoặc gộp.
 *
 * Hai việc dùng chung màn hình này nhưng lọc ngược nhau:
 *   - Chuyển bàn: chỉ chọn được bàn <b>trống</b>. Kéo đơn sang bàn đang có khách là
 *     lẫn tiền của hai nhóm.
 *   - Gộp bàn: chỉ chọn được bàn <b>đang có khách</b>. Gộp vào bàn trống thì đúng ra
 *     là chuyển bàn, không phải gộp.
 */
export function TablePicker({ mode, fromTableId, onPick, onClose }: Props) {
  const now = useNow(30_000)

  const zones = useLiveQuery(() => db.zones.orderBy('sortOrder').toArray(), [], [])
  const tables = useLiveQuery(() => db.diningTables.orderBy('sortOrder').toArray(), [], [])
  const openOrders = useLiveQuery(
    () => db.orders.where('status').equals('Open').toArray(),
    [],
    [] as Order[],
  )

  const orderOf = (tableId: string) => openOrders.find((o) => o.tableId === tableId)

  const choices = tables.filter((t) => {
    if (t.id === fromTableId) return false
    const busy = !!orderOf(t.id)
    return mode === 'move' ? !busy : busy
  })

  const zoneName = (id: string) => zones.find((z) => z.id === id)?.name ?? ''

  return (
    <Sheet title={mode === 'move' ? 'Chuyển sang bàn nào' : 'Gộp vào bàn nào'} onClose={onClose}>
      <p className="sheet-note">
        {mode === 'move'
          ? 'Chỉ hiện bàn đang trống. Cả đơn sẽ dời sang đó, món và giờ ngồi giữ nguyên.'
          : 'Chỉ hiện bàn đang có khách. Món của bàn này dồn hết sang bàn được chọn.'}
      </p>

      {choices.length === 0 ? (
        <div className="empty">
          <TableIcon className="empty-icon" />
          <p>{mode === 'move' ? 'Không còn bàn trống nào.' : 'Không có bàn nào khác đang có khách.'}</p>
        </div>
      ) : (
        <div className="list">
          {choices.map((t) => {
            const o = orderOf(t.id)
            return (
              <button
                key={t.id}
                type="button"
                className="row-btn"
                onClick={() => void onPick(t, o)}
              >
                <TableIcon icon={t.icon} className="row-icon" />
                <span className="row-text">
                  <b>{t.name}</b>
                  <small>
                    {zoneName(t.zoneId)}
                    {o ? ` · ngồi ${elapsedText(o.openedAt, now)}` : t.seats > 0 ? ` · ${t.seats} chỗ` : ''}
                  </small>
                </span>
                {o && <span className="row-price num">{money(o.total)}</span>}
              </button>
            )
          })}
        </div>
      )}
    </Sheet>
  )
}
